/**
 * Network validation utilities for IPs, CIDRs, MACs, VLANs, and ports.
 */

// IPv4 address validation
export const isValidIPv4 = (ip: string): boolean => {
  if (!ip || typeof ip !== 'string') return false;
  const trimmed = ip.trim();
  const pattern = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/;
  return pattern.test(trimmed);
};

// Simplified IPv6 validation (covers most common formats)
export const isValidIPv6 = (ip: string): boolean => {
  if (!ip || typeof ip !== 'string') return false;
  const trimmed = ip.trim();
  // Simplified regex for common IPv6 formats including :: notation
  const pattern = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]+|::(ffff(:0{1,4})?:)?((25[0-5]|(2[0-4]|1?\d)?\d)\.){3}(25[0-5]|(2[0-4]|1?\d)?\d)|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1?\d)?\d)\.){3}(25[0-5]|(2[0-4]|1?\d)?\d))$/;
  return pattern.test(trimmed);
};

// Combined IP validation (IPv4 or IPv6)
export const isValidIP = (ip: string): boolean => {
  return isValidIPv4(ip) || isValidIPv6(ip);
};

// CIDR notation validation (e.g., 192.168.1.0/24)
export const isValidCIDR = (cidr: string): boolean => {
  if (!cidr || typeof cidr !== 'string') return false;
  const trimmed = cidr.trim();
  
  // Check for IPv4 CIDR
  const ipv4CidrPattern = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\/([0-9]|[1-2][0-9]|3[0-2])$/;
  if (ipv4CidrPattern.test(trimmed)) {
    return true;
  }
  
  // Basic IPv6 CIDR check
  const ipv6CidrPattern = /^[0-9a-fA-F:]+\/([0-9]|[1-9][0-9]|1[0-1][0-9]|12[0-8])$/;
  if (ipv6CidrPattern.test(trimmed)) {
    const [ip] = trimmed.split('/');
    return isValidIPv6(ip);
  }
  
  return false;
};

// MAC address validation (supports multiple formats)
export const isValidMAC = (mac: string): boolean => {
  if (!mac || typeof mac !== 'string') return false;
  const trimmed = mac.trim().toUpperCase();
  
  // Colon-separated: 00:1A:2B:3C:4D:5E
  const colonPattern = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;
  // Dash-separated: 00-1A-2B-3C-4D-5E
  const dashPattern = /^([0-9A-F]{2}-){5}[0-9A-F]{2}$/;
  // Dot-separated (Cisco style): 001A.2B3C.4D5E
  const dotPattern = /^([0-9A-F]{4}\.){2}[0-9A-F]{4}$/;
  // No separator: 001A2B3C4D5E
  const noSepPattern = /^[0-9A-F]{12}$/;
  
  return colonPattern.test(trimmed) || 
         dashPattern.test(trimmed) || 
         dotPattern.test(trimmed) ||
         noSepPattern.test(trimmed);
};

// Port validation (single port, range, or "any")
export const isValidPort = (port: string): boolean => {
  if (!port || typeof port !== 'string') return false;
  const trimmed = port.trim().toLowerCase();
  
  if (trimmed === 'any' || trimmed === '*') return true;
  
  // Port range: 80-443
  if (trimmed.includes('-')) {
    const parts = trimmed.split('-');
    if (parts.length !== 2) return false;
    const start = parseInt(parts[0], 10);
    const end = parseInt(parts[1], 10);
    return !isNaN(start) && !isNaN(end) && start >= 1 && end <= 65535 && start <= end;
  }
  
  // Comma-separated ports: 80,443,8080
  if (trimmed.includes(',')) {
    const ports = trimmed.split(',');
    return ports.every(p => {
      const num = parseInt(p.trim(), 10);
      return !isNaN(num) && num >= 1 && num <= 65535;
    });
  }
  
  // Single port
  const num = parseInt(trimmed, 10);
  return !isNaN(num) && num >= 1 && num <= 65535;
};

// VLAN ID validation (1-4094, with 0 and 4095 reserved)
export const isValidVLAN = (vlan: string | number): boolean => {
  const num = typeof vlan === 'string' ? parseInt(vlan.trim(), 10) : vlan;
  return !isNaN(num) && num >= 1 && num <= 4094;
};

// Subnet mask validation (dotted decimal notation)
export const isValidSubnetMask = (mask: string): boolean => {
  if (!mask || typeof mask !== 'string') return false;
  const trimmed = mask.trim();
  
  // Valid subnet masks
  const validMasks = [
    '0.0.0.0', '128.0.0.0', '192.0.0.0', '224.0.0.0', '240.0.0.0',
    '248.0.0.0', '252.0.0.0', '254.0.0.0', '255.0.0.0', '255.128.0.0',
    '255.192.0.0', '255.224.0.0', '255.240.0.0', '255.248.0.0', '255.252.0.0',
    '255.254.0.0', '255.255.0.0', '255.255.128.0', '255.255.192.0', '255.255.224.0',
    '255.255.240.0', '255.255.248.0', '255.255.252.0', '255.255.254.0', '255.255.255.0',
    '255.255.255.128', '255.255.255.192', '255.255.255.224', '255.255.255.240',
    '255.255.255.248', '255.255.255.252', '255.255.255.254', '255.255.255.255'
  ];
  
  return validMasks.includes(trimmed);
};

// Default gateway validation (must be valid IP)
export const isValidGateway = (gateway: string): boolean => {
  return isValidIPv4(gateway);
};

// Firewall source/destination validation (any, IP, or CIDR)
export const isValidFirewallAddress = (addr: string): boolean => {
  if (!addr || typeof addr !== 'string') return false;
  const trimmed = addr.trim().toLowerCase();
  
  if (trimmed === 'any' || trimmed === '*') return true;
  return isValidIP(addr) || isValidCIDR(addr);
};

// Format MAC address to consistent colon-separated uppercase
export const formatMAC = (mac: string): string => {
  if (!mac) return '';
  
  // Remove all separators and convert to uppercase
  const cleaned = mac.replace(/[-:.]/g, '').toUpperCase();
  
  // Check if it's 12 hex characters
  if (!/^[0-9A-F]{12}$/.test(cleaned)) return mac;
  
  // Format as colon-separated
  return cleaned.match(/.{1,2}/g)?.join(':') || mac;
};

// Format IP address (trim whitespace)
export const formatIP = (ip: string): string => {
  return ip?.trim() || '';
};

// Validation result type
export interface ValidationResult {
  valid: boolean;
  message?: string;
}

// Comprehensive validators returning detailed results
export const validateIP = (ip: string): ValidationResult => {
  if (!ip || !ip.trim()) return { valid: true }; // Empty is valid (optional field)
  if (isValidIPv4(ip)) return { valid: true };
  if (isValidIPv6(ip)) return { valid: true };
  return { valid: false, message: 'Invalid IP address format. Example: 192.168.1.1' };
};

export const validateCIDR = (cidr: string): ValidationResult => {
  if (!cidr || !cidr.trim()) return { valid: true };
  if (isValidCIDR(cidr)) return { valid: true };
  return { valid: false, message: 'Invalid CIDR format. Example: 192.168.1.0/24' };
};

export const validateMAC = (mac: string): ValidationResult => {
  if (!mac || !mac.trim()) return { valid: true };
  if (isValidMAC(mac)) return { valid: true };
  return { valid: false, message: 'Invalid MAC address. Example: 00:1A:2B:3C:4D:5E' };
};

export const validatePort = (port: string): ValidationResult => {
  if (!port || !port.trim()) return { valid: true };
  if (isValidPort(port)) return { valid: true };
  return { valid: false, message: 'Invalid port. Use 1-65535, range (80-443), or "any"' };
};

export const validateVLAN = (vlan: string | number): ValidationResult => {
  if (vlan === '' || vlan === undefined || vlan === null) return { valid: true };
  if (isValidVLAN(vlan)) return { valid: true };
  return { valid: false, message: 'Invalid VLAN ID. Must be 1-4094' };
};

export const validateSubnetMask = (mask: string): ValidationResult => {
  if (!mask || !mask.trim()) return { valid: true };
  if (isValidSubnetMask(mask)) return { valid: true };
  return { valid: false, message: 'Invalid subnet mask. Example: 255.255.255.0' };
};

export const validateGateway = (gateway: string): ValidationResult => {
  if (!gateway || !gateway.trim()) return { valid: true };
  if (isValidGateway(gateway)) return { valid: true };
  return { valid: false, message: 'Invalid gateway IP. Example: 192.168.1.1' };
};

export const validateFirewallAddress = (addr: string): ValidationResult => {
  if (!addr || !addr.trim()) return { valid: true };
  if (isValidFirewallAddress(addr)) return { valid: true };
  return { valid: false, message: 'Use "any", valid IP, or CIDR format' };
};
